'use client';

import {clsx} from '@gfiber/typesmith';
import {
  forwardRef,
  type ReactNode,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import {FieldHints} from '../../FieldHints/FieldHints';
import styles from './TextArea.module.scss';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  helpTextClass?: string;
  errorTextClass?: string;
  invalid?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (props, forwardedRef) => {
    const {
      label,
      id,
      helpText,
      errorText,
      helpTextClass,
      errorTextClass,
      invalid,
      className,
      rows,
      required,
      ...rest
    } = props;

    const baseId = useId();
    const helpTextId = `${baseId}-help`;
    const errorTextId = `${baseId}-error`;
    const ariaDescribedBy =
      [helpText ? helpTextId : '', invalid && errorText ? errorTextId : '']
        .filter(Boolean)
        .join(' ') || undefined;

    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div
            className={clsx(
              className,
              styles.textfield,
              invalid && styles.hasError,
            )}
          >
            <textarea
              {...rest}
              id={id}
              aria-describedby={ariaDescribedBy}
              required={required}
              ref={forwardedRef}
              rows={rows}
              className={styles.textFieldInput}
              placeholder={rest.placeholder || ' '}
              aria-invalid={invalid}
            />

            {label && (
              <label
                className={styles.textFieldLabel}
                htmlFor={id}
              >
                {label}
              </label>
            )}
          </div>
        </div>
        <FieldHints
          helpText={helpText}
          errorText={invalid ? errorText : ''}
          helpTextClass={helpTextClass}
          errorTextClass={errorTextClass}
          helpTextId={helpTextId}
          errorTextId={errorTextId}
          className={styles.fieldHints}
        />
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';
